// Ported from LC2's MetricsGraph (commons/components/@extended/MetricsGraph.js), used by
// DaysOnMarket.js's `graph_data` field. LC2 rendered this with a recharts ComposedChart
// (multi-series bars + a zero reference line + a custom tooltip/legend). recharts isn't a
// dependency of this app (see the task's charting-library rule) — this component keeps the
// same data shape (`{data: [{label, ...seriesValues}], colors: {seriesKey: hex}}`) and the
// same "series can go negative around a zero baseline" behavior, but is a from-scratch
// flexbox/CSS grouped bar chart (same hand-rolled-scale approach as ../Offers/
// OffersTimeline.js) instead of a charting library: each label gets one bar per series,
// scaled against the shared min/max across all points, split around a zero baseline row.
// The recharts custom tooltip-on-hover is dropped in favor of a native `title` attribute on
// each bar (still surfaces the exact value, just via a plain browser tooltip).
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Box, Stack, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';

const CHART_HEIGHT = 180;

export const MetricsBarChart = ({graphData, name}) => {
    const {data = [], colors = {}} = graphData || {};
    const seriesKeys = Object.keys(colors);

    const {maxValue, minValue} = useMemo(() => {
        let max = 0;
        let min = 0;
        data.forEach((point) => {
            seriesKeys.forEach((key) => {
                const value = Number(point[key]);
                if (!Number.isFinite(value)) return;
                if (value > max) max = value;
                if (value < min) min = value;
            });
        });
        return {maxValue: max || 1, minValue: min};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data), seriesKeys.join(',')]);

    const positiveSpan = maxValue;
    const negativeSpan = Math.abs(minValue);
    const totalSpan = positiveSpan + negativeSpan || 1;
    const positiveHeight = (positiveSpan / totalSpan) * CHART_HEIGHT;
    const negativeHeight = CHART_HEIGHT - positiveHeight;

    if (data.length === 0 || seriesKeys.length === 0) return null;

    return (
        <Box sx={{width: '100%', pt: 2, pb: 1, borderRadius: '8px', border: `1px solid ${tokens.color.neutral200}`}}>
            <Typography sx={{width: '100%', opacity: 0.6, fontWeight: 400}} textAlign="center" variant="subtitle1">
                {name}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{px: 2, py: 0.5}} flexWrap="wrap">
                {seriesKeys.map((key) => (
                    <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{width: 10, height: 10, borderRadius: '2px', backgroundColor: colors[key]}} />
                        <Typography variant="caption" color="text.secondary">
                            {key}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
            <Box sx={{display: 'flex', alignItems: 'stretch', height: CHART_HEIGHT, px: 2, gap: 1, overflowX: 'auto'}}>
                {data.map((point, idx) => (
                    <Stack key={point.fullDate || point.label || idx} sx={{flex: '1 0 auto', minWidth: 36}} justifyContent="flex-end">
                        <Box sx={{height: positiveHeight, display: 'flex', alignItems: 'flex-end', gap: '2px', justifyContent: 'center'}}>
                            {seriesKeys.map((key) => {
                                const value = Number(point[key]) || 0;
                                if (value < 0) return <Box key={key} sx={{width: 8}} />;
                                const barHeight = positiveSpan > 0 ? Math.max(2, (value / positiveSpan) * positiveHeight) : 2;
                                return (
                                    <Box
                                        key={key}
                                        title={`${key}: ${value}`}
                                        sx={{width: 8, height: barHeight, backgroundColor: colors[key], borderRadius: '2px 2px 0 0'}}
                                    />
                                );
                            })}
                        </Box>
                        {negativeHeight > 0 && (
                            <Box sx={{height: negativeHeight, display: 'flex', alignItems: 'flex-start', gap: '2px', justifyContent: 'center', borderTop: `1px solid ${tokens.color.neutral300}`}}>
                                {seriesKeys.map((key) => {
                                    const value = Number(point[key]) || 0;
                                    if (value >= 0) return <Box key={key} sx={{width: 8}} />;
                                    const barHeight = negativeSpan > 0 ? Math.max(2, (Math.abs(value) / negativeSpan) * negativeHeight) : 2;
                                    return (
                                        <Box
                                            key={key}
                                            title={`${key}: ${value}`}
                                            sx={{width: 8, height: barHeight, backgroundColor: colors[key], borderRadius: '0 0 2px 2px'}}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" textAlign="center" noWrap sx={{mt: 0.5, fontSize: '10px'}}>
                            {point.label}
                        </Typography>
                    </Stack>
                ))}
            </Box>
        </Box>
    );
};

MetricsBarChart.propTypes = {
    graphData: PropTypes.shape({
        data: PropTypes.array,
        colors: PropTypes.object
    }),
    name: PropTypes.string
};

export default MetricsBarChart;
